import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';
import { generalStatsApi } from '../../services';

const FilterOptionSchema = z.object({
	value: z.string(),
	label: z.string(),
}).strip();

const MajorFilterOptionSchema = FilterOptionSchema.extend({
	maKhoa: z.string().optional().default(''),
}).strip();

const FiltersStateSchema = z.object({
	years: z.array(z.number()),
	faculties: z.array(FilterOptionSchema),
	majors: z.array(MajorFilterOptionSchema),
	provinces: z.array(FilterOptionSchema),
	mergedProvinces: z.array(FilterOptionSchema),
	selectedFaculty: z.string().nullable(),
	loading: z.boolean(),
	error: z.string().nullable(),
	initialized: z.boolean(),
}).strip();

type FiltersState = z.infer<typeof FiltersStateSchema>;

const initialState: FiltersState = {
	years: [],
	faculties: [],
	majors: [],
	provinces: [],
	mergedProvinces: [],
	selectedFaculty: null,
	loading: false,
	error: null,
	initialized: false,
};

export const fetchAllFilters = createAsyncThunk(
	'filters/fetchAll',
	async (_, { rejectWithValue }) => {
		try {
			const response = await generalStatsApi.getAllFilters();
			return response.data;
		} catch (error) {
			return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch filters');
		}
	}
);

const filtersSlice = createSlice({
	name: 'filters',
	initialState,
	reducers: {
		setSelectedFaculty: (state, action) => {
			state.selectedFaculty = action.payload;
		},
		clearSelectedFaculty: (state) => {
			state.selectedFaculty = null;
		},
		initializeFacultyForUser: (state, action) => {
			const facultyCode = action.payload;
			if (facultyCode && !state.selectedFaculty) {
				state.selectedFaculty = facultyCode;
			}
		},
		resetFilters: (state) => {
			state.years = [];
			state.faculties = [];
			state.majors = [];
			state.provinces = [];
			state.mergedProvinces = [];
			state.selectedFaculty = null;
			state.loading = false;
			state.error = null;
			state.initialized = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAllFilters.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAllFilters.fulfilled, (state, action) => {
				state.years = action.payload.years || [];
				state.faculties = (action.payload.faculties || []).sort((a, b) => a.value.localeCompare(b.value));
				state.majors = (action.payload.majors || []).sort((a, b) => a.value.localeCompare(b.value));
				state.provinces = action.payload.provinces || [];
				state.mergedProvinces = action.payload.mergedProvinces || [];
				state.loading = false;
				state.initialized = true;
			})
			.addCase(fetchAllFilters.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const { setSelectedFaculty, clearSelectedFaculty, initializeFacultyForUser, resetFilters } = filtersSlice.actions;
export default filtersSlice.reducer;
